/**
 * KpiCard — Key Performance Indicator stat card
 * Shows a big value with label, min/max chips, and status.
 */

import './KpiCard.css';

export type KpiStatus = 'active' | 'warning' | 'fault' | 'idle';

export interface KpiCardProps {
  title: string;
  value: number | string;
  unit: string;
  min?: number;
  max?: number;
  status?: KpiStatus;
  /** Optional icon (any ReactNode, e.g. an SVG) */
  icon?: React.ReactNode;
}

function statusLabel(s: KpiStatus) {
  const map: Record<KpiStatus, string> = {
    active: 'Active',
    warning: 'Warning',
    fault: 'Fault',
    idle: 'Idle',
  };
  return map[s] ?? s;
}

export default function KpiCard({ title, value, unit, min, max, status = 'active', icon }: KpiCardProps) {
  const formattedValue = typeof value === 'number' ? value.toFixed(1) : value;

  return (
    <div className={`kpi-card card kpi-card--${status}`}>
      {/* Header row */}
      <div className="kpi-card__header">
        <span className="kpi-card__title">{title}</span>
        {icon && <span className="kpi-card__icon">{icon}</span>}
      </div>

      {/* Big value */}
      <div className="kpi-card__value-row">
        <span className="kpi-card__value tabular">{formattedValue}</span>
        <span className="kpi-card__unit">{unit}</span>
      </div>

      {/* Meta chips */}
      <div className="kpi-card__meta">
        {min !== undefined && (
          <span className="kpi-chip kpi-chip--muted">
            Min {min.toFixed(1)}
          </span>
        )}
        {max !== undefined && (
          <span className="kpi-chip kpi-chip--muted">
            Max {max.toFixed(1)}
          </span>
        )}
        <span className={`kpi-chip kpi-chip--status kpi-chip--${status}`}>
          {statusLabel(status)}
        </span>
      </div>
    </div>
  );
}
